import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import Title from '@/components/ui/Title';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Pagination } from '@/components/ui/pagination';
import { FileText, Link2, Loader2, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import {
  createSharedResourceLink,
  deleteSharedResource,
  finalizeSharedResourceFileUpload,
  initSharedResourceFileUpload,
  listSharedResources,
} from '@/features/webinar/webinarThunks';
import {
  isAllowedSharedResourceFileType,
  isSharedResourceLinkFormValid,
  isValidSharedResourceLinkUrl,
  isValidSharedResourceFileName,
  isValidSharedResourceFileSize,
  SHARED_RESOURCE_FILE_ACCEPT,
} from '@/lib/sharedResourceValidation';
import { nextPageAfterDelete } from '@/lib/sharedResourcePagination';
import { getEvergreenTimelineResourceIds } from '@/features/webinar/evergreenTimelineUtils';
import TimeCodeComponent, { type TimeCodeScheduling } from '../../evergreen/TimeCodeComponent';

const bytesToReadable = (bytes: number | null): string => {
  if (!bytes || bytes <= 0) return '';
  if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  return (bytes / 1024).toFixed(0) + ' KB';
};

const uploadFileToUrl = async (
  url: string,
  file: File,
  requiredHeaders: Record<string, string>,
) => {
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Cache-Control': 'public, max-age=31536000, immutable',
      ...requiredHeaders,
    },
    body: file,
  });
  if (!response.ok) {
    throw new Error('upload-failed');
  }
};

export default function SharedResourcesForm({
  webinarId,
  workspaceId,
  isEdit,
  isEvergreen,
  scheduling,
  videoUrl,
  videoFallbackUrl,
  readOnly,
}: {
  webinarId: number;
  workspaceId: number;
  isEdit?: boolean;
  isEvergreen?: boolean;
  scheduling?: TimeCodeScheduling;
  videoUrl?: string;
  videoFallbackUrl?: string;
  readOnly?: boolean;
}) {
  const { t } = useTranslation('createWebinar');
  const dispatch = useAppDispatch();
  const locked = readOnly === true;

  const { sharedResources, sharedResourcesLoading, sharedResourcesTotalPages } = useAppSelector(
    (state: RootState) => state.webinar,
  );
  const evergreenTimeline = useAppSelector((state: RootState) => state.webinar.evergreenTimeline);
  const evergreenSharedResourceIds = useMemo(
    () =>
      isEvergreen && !scheduling
        ? getEvergreenTimelineResourceIds(evergreenTimeline, 'SHARED_RESOURCE')
        : null,
    [isEvergreen, scheduling, evergreenTimeline],
  );
  const visibleResources = useMemo(
    () =>
      evergreenSharedResourceIds
        ? sharedResources.filter((resource) => evergreenSharedResourceIds.has(resource.id))
        : sharedResources,
    [sharedResources, evergreenSharedResourceIds],
  );

  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [page, setPage] = useState(0);

  // Champs du dialog fichier
  const [fileTitle, setFileTitle] = useState('');
  const [fileDescription, setFileDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Champs du dialog lien
  const [linkTitle, setLinkTitle] = useState('');
  const [linkDescription, setLinkDescription] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  useEffect(() => {
    void dispatch(listSharedResources({ workspaceId, webinarId, page }));
  }, [dispatch, webinarId, workspaceId, page]);

  const resetFileDialog = () => {
    setFileTitle('');
    setFileDescription('');
    setSelectedFile(null);
  };

  const resetLinkDialog = () => {
    setLinkTitle('');
    setLinkDescription('');
    setLinkUrl('');
  };

  const handleAddFile = async () => {
    const title = fileTitle.trim();
    if (!title) {
      toast.error(t('sharedResourcesForm.errors.titleRequired'));
      return;
    }
    if (!selectedFile) {
      toast.error(t('sharedResourcesForm.errors.fileRequired'));
      return;
    }
    if (!isAllowedSharedResourceFileType(selectedFile)) {
      toast.error(t('sharedResourcesForm.errors.fileTypeNotAllowed'));
      return;
    }
    if (!isValidSharedResourceFileSize(selectedFile.size)) {
      toast.error(t('sharedResourcesForm.errors.fileTooLarge'));
      return;
    }
    if (!isValidSharedResourceFileName(selectedFile.name)) {
      toast.error(t('sharedResourcesForm.errors.generic'));
      return;
    }

    setIsSubmitting(true);
    try {
      const init = await dispatch(
        initSharedResourceFileUpload({
          workspaceId,
          webinarId,
          input: {
            title,
            description: fileDescription.trim() || null,
            fileName: selectedFile.name,
            mimeType: selectedFile.type || 'application/octet-stream',
            sizeBytes: selectedFile.size,
          },
        }),
      ).unwrap();

      await uploadFileToUrl(init.uploadUrl, selectedFile, init.requiredHeaders);

      await dispatch(
        finalizeSharedResourceFileUpload({
          workspaceId,
          webinarId,
          sharedResourceId: init.sharedResourceId,
        }),
      ).unwrap();

      toast.success(t('sharedResourcesForm.success.fileAdded'));
      resetFileDialog();
      setFileDialogOpen(false);
      void dispatch(listSharedResources({ workspaceId, webinarId, page }));
    } catch {
      toast.error(t('sharedResourcesForm.errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddLink = async () => {
    const title = linkTitle.trim();
    const url = linkUrl.trim();
    if (!isSharedResourceLinkFormValid(title, url)) {
      return;
    }

    setIsSubmitting(true);
    try {
      await dispatch(
        createSharedResourceLink({
          workspaceId,
          webinarId,
          input: {
            title,
            description: linkDescription.trim() || null,
            externalUrl: url,
          },
        }),
      ).unwrap();

      toast.success(t('sharedResourcesForm.success.linkAdded'));
      resetLinkDialog();
      setLinkDialogOpen(false);
      void dispatch(listSharedResources({ workspaceId, webinarId, page }));
    } catch {
      toast.error(t('sharedResourcesForm.errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (sharedResourceId: number) => {
    setDeletingId(sharedResourceId);
    try {
      await dispatch(deleteSharedResource({ workspaceId, webinarId, sharedResourceId })).unwrap();
      toast.success(t('sharedResourcesForm.success.deleted'));
      const targetPage = nextPageAfterDelete(page, sharedResources.length);
      if (targetPage !== page) {
        setPage(targetPage);
      } else {
        void dispatch(listSharedResources({ workspaceId, webinarId, page }));
      }
    } catch {
      toast.error(t('sharedResourcesForm.errors.generic'));
    } finally {
      setDeletingId(null);
    }
  };

  if (sharedResourcesLoading && sharedResources.length === 0) {
    return (
      <div>
        <Accordion type="single" collapsible>
          <AccordionItem
            value="item-1"
            className={`bg-white p-0 ${isEdit ? 'accordion-page' : ''}`}
          >
            <AccordionTrigger className="rounded-[20px]">
              <Title level={2}>{t('sharedResourcesForm.title')}</Title>
            </AccordionTrigger>
            <AccordionContent className="flex flex-col gap-4 rounded-[20px] bg-white pt-4 p-0">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    );
  }

  return (
    <div>
      <Accordion type="single" collapsible>
        <AccordionItem value="item-1" className={`bg-white p-0 ${isEdit ? 'accordion-page' : ''}`}>
          <AccordionTrigger className="rounded-[20px]" fillForm={visibleResources.length > 0}>
            <div>
              <Title level={2}>{t('sharedResourcesForm.title')}</Title>
              <p className="text-sm font-normal text-tertiary">
                {t('sharedResourcesForm.description')}
              </p>
            </div>
          </AccordionTrigger>
          <AccordionContent
            className={`flex flex-col gap-4 rounded-[20px] bg-white p-0 ${
              isEdit ? 'pb-4 bg-subtitle' : ''
            }`}
          >
            <div className="space-y-[10px] pt-2">
              {visibleResources.length === 0 ? (
                <p className="text-sm text-tertiary">{t('sharedResourcesForm.empty')}</p>
              ) : (
                visibleResources.map((resource) => (
                  <div key={resource.id}>
                    <div className="p-4 bg-white border border-medium rounded-2xl flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="size-10 shrink-0 bg-medium rounded-lg flex items-center justify-center">
                          {resource.type === 'FILE' ? (
                            <FileText className="size-5" />
                          ) : (
                            <Link2 className="size-5" />
                          )}
                        </div>
                        <div className="flex flex-col gap-1 min-w-0">
                          <span className="truncate">{resource.title}</span>
                          <p className="text-sm text-gray-tertiary truncate">
                            {resource.type === 'FILE'
                              ? [resource.originalFileName, bytesToReadable(resource.sizeBytes)]
                                  .filter(Boolean)
                                  .join(' · ')
                              : resource.externalUrl}
                          </p>
                        </div>
                      </div>
                      {!locked && (
                        <button
                          type="button"
                          aria-label={t('sharedResourcesForm.delete')}
                          className="shrink-0 cursor-pointer disabled:cursor-not-allowed"
                          disabled={deletingId === resource.id}
                          onClick={() => handleDelete(resource.id)}
                        >
                          {deletingId === resource.id ? (
                            <Loader2 className="size-5 animate-spin" />
                          ) : (
                            <Trash2 className="size-5" />
                          )}
                        </button>
                      )}
                    </div>
                    {videoUrl && (
                      <TimeCodeComponent
                        videoUrl={videoUrl}
                        scheduling={scheduling}
                        videoFallbackUrl={videoFallbackUrl}
                        selectedEventType="SHARED_RESOURCE"
                        selectedResourceId={resource.id}
                        readOnly={locked}
                      />
                    )}
                  </div>
                ))
              )}
            </div>

            <Pagination
              currentPage={page}
              totalPages={sharedResourcesTotalPages}
              onPageChange={setPage}
            />

            {(!isEvergreen || !!scheduling) && !locked && (
              <div className="flex flex-wrap items-center gap-3 pt-2">
                {/* Ajouter un fichier */}
                <Dialog
                  open={fileDialogOpen}
                  onOpenChange={(open) => {
                    setFileDialogOpen(open);
                    if (!open) resetFileDialog();
                  }}
                >
                  <DialogTrigger asChild>
                    <Button type="button" variant="outline" className="gap-2">
                      <FileText className="size-4" />
                      {t('sharedResourcesForm.addFile')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('sharedResourcesForm.fileDialog.title')}</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col gap-2">
                        <Label>{t('sharedResourcesForm.fields.title')}</Label>
                        <Input
                          value={fileTitle}
                          onChange={(e) => setFileTitle(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <Label>{t('sharedResourcesForm.fileDialog.chooseFile')}</Label>
                        <Input
                          type="file"
                          accept={SHARED_RESOURCE_FILE_ACCEPT}
                          disabled={isSubmitting}
                          onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                        />
                        <p className="text-xs text-tertiary">
                          {t('sharedResourcesForm.fileDialog.allowedFormats')}
                        </p>
                        {selectedFile && (
                          <p className="text-sm text-tertiary">
                            {selectedFile.name} · {bytesToReadable(selectedFile.size)}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Label>{t('sharedResourcesForm.fields.description')}</Label>
                        <Textarea
                          value={fileDescription}
                          onChange={(e) => setFileDescription(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                    <DialogFooter className="gap-2">
                      <DialogClose asChild>
                        <Button type="button" variant="outline" disabled={isSubmitting}>
                          {t('common:cancel', { defaultValue: 'Annuler' })}
                        </Button>
                      </DialogClose>
                      <Button type="button" onClick={handleAddFile} disabled={isSubmitting}>
                        {isSubmitting && <Loader2 className="size-4 animate-spin" />}
                        {t('sharedResourcesForm.fileDialog.submit')}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Ajouter un lien */}
                <Dialog
                  open={linkDialogOpen}
                  onOpenChange={(open) => {
                    setLinkDialogOpen(open);
                    if (!open) resetLinkDialog();
                  }}
                >
                  <DialogTrigger asChild>
                    <Button type="button" variant="outline" className="gap-2">
                      <Link2 className="size-4" />
                      {t('sharedResourcesForm.addLink')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('sharedResourcesForm.linkDialog.title')}</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col gap-2">
                        <Label>{t('sharedResourcesForm.fields.title')}</Label>
                        <Input
                          value={linkTitle}
                          onChange={(e) => setLinkTitle(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <Label>{t('sharedResourcesForm.fields.url')}</Label>
                        <Input
                          value={linkUrl}
                          placeholder="https://..."
                          onChange={(e) => setLinkUrl(e.target.value)}
                          disabled={isSubmitting}
                        />
                        {linkUrl.trim().length > 0 && !isValidSharedResourceLinkUrl(linkUrl) && (
                          <p className="text-sm text-red-500">
                            {t('sharedResourcesForm.errors.urlInvalid')}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Label>{t('sharedResourcesForm.fields.description')}</Label>
                        <Textarea
                          value={linkDescription}
                          onChange={(e) => setLinkDescription(e.target.value)}
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                    <DialogFooter className="gap-2">
                      <DialogClose asChild>
                        <Button type="button" variant="outline" disabled={isSubmitting}>
                          {t('common:cancel', { defaultValue: 'Annuler' })}
                        </Button>
                      </DialogClose>
                      <Button
                        type="button"
                        onClick={handleAddLink}
                        disabled={
                          isSubmitting || !isSharedResourceLinkFormValid(linkTitle, linkUrl)
                        }
                      >
                        {isSubmitting && <Loader2 className="size-4 animate-spin" />}
                        {t('sharedResourcesForm.linkDialog.submit')}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
